import React, { useEffect, useMemo, useCallback } from "react";
import { Card, Typography, Table, Tag, Spin, Alert, Row, Col, Select, Space } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { reducer, sliceKey, actions } from "./slice";
import { geographyAnalyticsSaga } from "./saga";
import {
  selectGeographyData,
  selectLoading,
  selectError,
  selectPagination, 
} from "./selectors";

const { Title, Text } = Typography;
const { Option } = Select;

// Same color function as used in the India map component
const getColor = (rate) => {
    if (rate === undefined || rate === null || rate === "No data") return "#ffffff"; // White for no data
    
    // Ensure rate is a number and clamp between 0 and 100
    const numRate = Math.max(0, Math.min(100, Number(rate)));
    
    // Create gradient color mapping
    if (numRate >= 90) {
        // Green gradient (90-100): darker to lighter green
        const intensity = (numRate - 90) / 10; // 0 to 1
        const r = Math.round(46 - (46 - 39) * intensity);   // 46 to 39
        const g = Math.round(204 - (204 - 174) * intensity); // 204 to 174  
        const b = Math.round(113 - (113 - 96) * intensity);  // 113 to 96
        return `rgb(${r}, ${g}, ${b})`;
    } else if (numRate >= 80) {
        // Yellow-Green gradient (80-90): yellow to green
        const intensity = (numRate - 80) / 10; // 0 to 1
        const r = Math.round(241 - (241 - 46) * intensity);  // 241 to 46
        const g = Math.round(196 - (196 - 204) * intensity); // 196 to 204
        const b = Math.round(15 - (15 - 113) * intensity);   // 15 to 113
        return `rgb(${r}, ${g}, ${b})`;
    } else if (numRate >= 70) {
        // Orange-Yellow gradient (70-80): orange to yellow
        const intensity = (numRate - 70) / 10; // 0 to 1
        const r = Math.round(230 - (230 - 241) * intensity); // 230 to 241
        const g = Math.round(126 - (126 - 196) * intensity); // 126 to 196
        const b = Math.round(34 - (34 - 15) * intensity);    // 34 to 15
        return `rgb(${r}, ${g}, ${b})`;
    } else if (numRate >= 50) {
        // Red-Orange gradient (50-70): red to orange
        const intensity = (numRate - 50) / 20; // 0 to 1
        const r = Math.round(231 - (231 - 230) * intensity); // 231 to 230
        const g = Math.round(76 - (76 - 126) * intensity);   // 76 to 126
        const b = Math.round(60 - (60 - 34) * intensity);    // 60 to 34
        return `rgb(${r}, ${g}, ${b})`;
    } else {
        // Dark red gradient (0-50): very dark red to red
        const intensity = numRate / 50; // 0 to 1
        const r = Math.round(139 + (231 - 139) * intensity); // 139 to 231
        const g = Math.round(26 + (76 - 26) * intensity);    // 26 to 76
        const b = Math.round(26 + (60 - 26) * intensity);    // 26 to 60
        return `rgb(${r}, ${g}, ${b})`;
    }
};

const columns = [
  {
    title: "State",
    dataIndex: "state",
    key: "state",
    render: (text) => <strong>{text}</strong>,
  },
  {
    title: "District",
    dataIndex: "district",
    key: "district",
  },
  {
    title: "Total Orders",
    dataIndex: "totalOrders",
    key: "totalOrders",
    align: "center",
  },
  {
    title: "Collection Initiated",
    dataIndex: "collectionInitiated",
    key: "collectionInitiated",
    align: "center",
  },
  {
    title: "Pending Orders",
    dataIndex: "pendingOrders",
    key: "pendingOrders",
    align: "center",
  },
  {
    title: "Samples Collected",
    dataIndex: "sampleCollected", 
    key: "sampleCollected",
    align: "center",
  },
  {
    title: "Ready for Pickup",
    dataIndex: "readyForPickup",
    key: "readyForPickup",
    align: "center",
  },
  {
    title: "In-Transit",
    dataIndex: "inTransit",
    key: "inTransit",
    align: "center",
  },
  {
    title: "Delivered to Lab",
    dataIndex: "deliveredToLab",
    key: "deliveredToLab",
    align: "center",
  },
  {
    title: "Under Correction",
    dataIndex: "needCorrection",
    key: "needCorrection",
    align: "center",
  },
  {
    title: "Regret Cases",
    dataIndex: "regretCases",
    key: "regretCases",
    align: "center",
  },
  {
  title: "Sampling Completion%",
  dataIndex: "sampleCollectionScore",
  key: "sampleCollectionScore",
  align: "center",
  render: (value) => {
    const textColor = getColor(value); 
    return (
      <span
        style={{
          color: textColor,
          fontWeight: 600,
        }}
      >
        {value}%
      </span>
    );
  },
},
  {
    title: "Completion%",
    dataIndex: "completionRate",
    key: "completionRate",
    align: "center",
    render: (value) => {
      const backgroundColor = getColor(value);
      // Calculate text color based on completion rate for better readability
      const numValue = Number(value) || 0;
      const textColor = numValue >= 70 ? "#fff" : "#000";
      
      return (
        <Tag
          color=""
          style={{
            backgroundColor: backgroundColor,
            color: textColor,
            fontWeight: 600,
            borderRadius: "12px",
            padding: "4px 12px",
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          {value}%
        </Tag>
      );
    },
  },
];

const GeographyAnalyticsTable = ({ filters = {} }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  
  // Inject reducer and saga
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: geographyAnalyticsSaga });

  const loading = useSelector(selectLoading);
  const data = useSelector(selectGeographyData);
  const error = useSelector(selectError);
  const pagination = useSelector(selectPagination);

  // Memoize the filters to prevent infinite renders
  const memoizedFilters = useMemo(() => ({
    companyIds: filters.companyIds || [],
    projectCodeIds: filters.projectCodeIds || [],
    selectedState: filters.selectedState || [],
    sortDescending: filters.sortDescending ?? true
  }), [
    filters.companyIds,
    filters.projectCodeIds, 
    filters.selectedState,
    filters.sortDescending
  ]);

  // Create a stable callback for navigation
  const handleBackClick = useCallback(() => {
    history.push("/bo/dashboard", { filters: memoizedFilters });
  }, [history, memoizedFilters]);

  // Handle pagination changes
  const handleTableChange = useCallback((page, pageSize) => {
    dispatch(actions.setPagination({ currentPage: page, pageSize }));
  }, [dispatch]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    dispatch(actions.setPageSize(newPageSize));
  }, [dispatch]);

  // Fetch data when memoized filters change
  useEffect(() => {
    dispatch(actions.fetchGeographyData({ 
      filters: memoizedFilters
    }));
  }, [dispatch, memoizedFilters]);

  // Reset pagination when filters change
  useEffect(() => {
    dispatch(actions.resetPagination());
  }, [dispatch, memoizedFilters]);

  // Memoized table data with unique keys
  const tableData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    let filtered = [...data];

    // Apply state filter if selectedState is an array and non-empty
    if (Array.isArray(memoizedFilters.selectedState) && memoizedFilters.selectedState.length > 0) {
      filtered = filtered.filter((item) =>
        memoizedFilters.selectedState.includes(item.state)
      );
    }

    return filtered.map((item, idx) => ({
      ...item,
      id: `${item.state}-${item.district}-${idx}`,
      key: `${item.state}-${item.district}-${idx}`,
    }));
  }, [data, memoizedFilters.selectedState]);

  // Pagination configuration for Ant Design Table
  const paginationConfig = {
    current: pagination.currentPage,
    pageSize: pagination.pageSize,
    total: tableData.length, // Use filtered data length
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => 
      `${range[0]}-${range[1]} of ${total} entries`,
    pageSizeOptions: ['10', '30', '50', '100'],
    onChange: handleTableChange,
    onShowSizeChange: handleTableChange,
    style: { marginTop: 16 }
  };

  return (
    <Card
      style={{ marginTop: 24 }}
      bodyStyle={{ padding: 0, backgroundColor: "#0D9488" }} // Teal-green background
    >
      <Row align="middle" style={{ padding: "16px 24px" }}>
        <Col>
          <ArrowLeftOutlined
            style={{
              fontSize: 18,
              color: "#fff",
              marginRight: 12,
              cursor: "pointer",
            }}
            onClick={handleBackClick}
          />
        </Col>
        <Col flex="auto">
          <Title level={4} style={{ color: "#fff", margin: 0 }}>
            Detailed Geography Analytics
          </Title>
        </Col>
        <Col>
          <Space align="center">
            <Text style={{ color: "#fff", fontSize: 14 }}>Show:</Text>
            <Select
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 80 }}
              size="small"
            >
              <Option value={10}>10</Option>
              <Option value={30}>30</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
            </Select>
            <Text style={{ color: "#fff", fontSize: 14 }}>entries</Text>
          </Space>
        </Col>
      </Row>

      {/* Main Content */}
      <div style={{ padding: 24, backgroundColor: "#fff" }}>
        {error && (
          <Alert
            message="Error loading data"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Summary Info */}
        {!loading && tableData.length > 0 && (
          <div style={{ marginBottom: 16, color: "#666" }}>
            <Text>
              Showing {Math.min(pagination.pageSize, tableData.length)} of {tableData.length} entries
              {memoizedFilters.selectedState?.length > 0 && 
                ` (filtered by ${memoizedFilters.selectedState.length} state${memoizedFilters.selectedState.length > 1 ? 's' : ''})`
              }
            </Text>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              Loading geography analytics data...
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={paginationConfig}
            rowKey="id"
            bordered={false}
            scroll={{ x: true }}
          />
        )}
      </div>
    </Card>
  );
};

export default GeographyAnalyticsTable;